/**
 * Espace Équipe (refonte v4 du 18/10/2026) : Disponibilités · Décisions ·
 * Astreintes (19/10/2026), chacun
 * à son adresse. Ce que les pages et les redirections partagent sans rien
 * dessiner : rubriques permises, anciennes adresses relues, jour consulté.
 * Testé sans navigateur.
 */
import type { Capability } from '@myequip/shared';
import { scheduleKindLabel } from '../../core/i18n.js';
import { eventRange } from '../planning/TimeGrid.js';
import type { PlanningEvent, PlanningIntervention } from '../planning/planning-data.js';

export const TEAM_PATHS = {
  disponibilites: '/equipe', decisions: '/equipe/decisions', astreintes: '/equipe/astreintes',
} as const;
export type TeamSection = keyof typeof TEAM_PATHS;

/**
 * Les rubriques de l'espace que ce compte peut ouvrir, dans l'ordre. Les
 * astreintes (19/10/2026) se lisent par tout membre — comme le planning : qui
 * ne voit que le sien n'y lit que les siennes —, elles ferment la liste.
 */
export function teamSections(can: (capability: Capability) => boolean): TeamSection[] {
  return [
    ...(can('planning.view_all') ? ['disponibilites' as const] : []),
    ...(can('absence.approve') ? ['decisions' as const] : []),
    'astreintes',
  ];
}

/** La rubrique ouverte d'après l'adresse. */
export function teamSectionOf(pathname: string): TeamSection {
  if (pathname.startsWith(TEAM_PATHS.decisions)) return 'decisions';
  if (pathname.startsWith(TEAM_PATHS.astreintes)) return 'astreintes';
  return 'disponibilites';
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** Anciens noms des décisions : l'avis (`eventId`) et l'onglet (`processed`). */
export const LEGACY_DECISION_PARAMS = { eventId: 'eventId' } as const;

/**
 * `/decisions` et `/validations` (avis, signets, accueil d'avant le 18/10/2026)
 * → `/equipe/decisions`, la demande désignée sous `id`, l'onglet sous ses
 * nouvelles valeurs.
 */
export function decisionsTarget(search: string): string {
  const params = new URLSearchParams(search);
  const next = new URLSearchParams();
  const id = params.get(LEGACY_DECISION_PARAMS.eventId) ?? params.get('id') ?? '';
  if (UUID.test(id)) next.set('id', id);
  const tab = params.get('onglet');
  if (tab === 'processed' || tab === 'traitees') next.set('onglet', 'traitees');
  const query = next.toString();
  return `${TEAM_PATHS.decisions}${query ? `?${query}` : ''}`;
}

/** `/personnel?date=` → `/equipe?date=` (la date gardée si elle est valide). */
export function availabilityTarget(search: string): string {
  const date = new URLSearchParams(search).get('date') ?? '';
  return `${TEAM_PATHS.disponibilites}${ISO_DAY.test(date) ? `?date=${date}` : ''}`;
}

/** Jour local « AAAA-MM-JJ ». */
export function localDay(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/** Le jour consulté : celui de l'adresse s'il existe au calendrier (pas un 31 février), sinon aujourd'hui. */
export function dayOf(value: string | null, today = new Date()): string {
  return value && ISO_DAY.test(value) && localDay(new Date(`${value}T12:00`)) === value ? value : localDay(today);
}

/** Une ligne de l'agenda d'un membre : son heure de début et ce qu'on en lit. */
export interface AgendaLine { at: string; text: string }

/**
 * L'agenda d'un membre au jour consulté, par heure : ses interventions (le
 * client, ou « Créneau occupé » pour un site dont on ne voit pas le client,
 * déjà nommé par `fetchPlanningData`) et ses temps bloqués.
 */
export function memberAgenda(
  missions: readonly Pick<PlanningIntervention, 'starts_at' | 'ends_at' | 'time_approx_minutes' | 'customer_name'>[],
  events: readonly Pick<PlanningEvent, 'starts_at' | 'ends_at' | 'time_approx_minutes' | 'title' | 'kind'>[],
): AgendaLine[] {
  return [
    ...missions.map((m) => ({ at: m.starts_at, text: `${eventRange(m.starts_at, m.ends_at, m.time_approx_minutes)} · ${m.customer_name}` })),
    ...events.map((e) => ({ at: e.starts_at, text: `${eventRange(e.starts_at, e.ends_at, e.time_approx_minutes)} · ${e.title || scheduleKindLabel(e.kind)}` })),
  ].sort((a, b) => a.at.localeCompare(b.at));
}

/** États d'intervention où la personne est sur place (« En intervention »). */
const ON_SITE = new Set(['en_cours', 'en_pause']);

/**
 * L'état d'un membre au jour consulté, du plus fort au plus faible. « En
 * intervention » est un état en direct : il ne vaut que pour aujourd'hui (une
 * fin oubliée sur un jour passé ne fait pas croire qu'il y est encore).
 */
export type MemberDayStatus = 'absent' | 'aValider' | 'enIntervention' | 'tempsBloque' | 'disponible';

export function memberDayStatus(row: {
  absent: boolean;
  pendingAbsence: boolean;
  blocked: boolean;
  isToday: boolean;
  missions: readonly Pick<PlanningIntervention, 'status'>[];
}): MemberDayStatus {
  if (row.absent) return 'absent';
  if (row.pendingAbsence) return 'aValider';
  if (row.isToday && row.missions.some((m) => ON_SITE.has(m.status))) return 'enIntervention';
  if (row.blocked) return 'tempsBloque';
  return 'disponible';
}

/**
 * La ligne « rôle · lieu de travail » d'un membre, sans redite : un rôle nommé
 * « Bureau » qui travaille au bureau se lit « Bureau », pas « Bureau · Bureau ».
 */
export function memberRoleLine(role: string, place: string): string {
  if (!role) return place;
  return role.trim().toLocaleLowerCase('fr') === place.toLocaleLowerCase('fr') ? role : `${role} · ${place}`;
}
